import React from 'react';
import { createPortal } from 'react-dom';
import { Trash2, X } from 'lucide-react';
import { AppSelectDropdown } from '../AppSelectDropdown';
import { DatePicker } from '../DatePicker';
import { EmployeeMultiSelect } from '../employees/EmployeeMultiSelect';
import type { EmployeeTimeEntryType } from '../employees/employeeTimeEntryConstants';
import { isHourTrackingEntryType, normalizeBreakMinutes } from '../../utils/employeeTimeEntryHours';
import type { BoardEmployee } from './plantafelTypes';
import { PLANTAFEL_ABSENCE_ENTRY_TYPES, type PlantafelAbsenceDraft } from './plantafelAbsenceMapping';
import { UI_Z_CLASS } from '../../utils/uiZIndex';
import { useDirtySnapshot, useDiscardGuard } from '../../hooks/useDiscardGuard';
import { ABSENCE_CONFLICT_TEXTS, useAssigneeConflictConfirm } from '../activities/useAssigneeConflictConfirm';

export type { PlantafelAbsenceDraft };

function buildDefaultDraft(employees: BoardEmployee[]): PlantafelAbsenceDraft {
  const today = new Date();
  const ymd = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
  return {
    employeeId: employees[0]?.id ?? '',
    dateFrom: ymd,
    dateTo: ymd,
    entryType: 'vacation',
    startTime: '',
    endTime: '',
    breakMinutes: '0',
    note: '',
    entryId: null,
    readOnly: false,
  };
}

export function PlantafelAbsenceDrawer({
  draft: initialDraft,
  employees,
  onClose,
  onSaved,
}: {
  draft: PlantafelAbsenceDraft | null;
  employees: BoardEmployee[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [form, setForm] = React.useState<PlantafelAbsenceDraft>(() =>
    initialDraft ?? buildDefaultDraft(employees),
  );
  const [saving, setSaving] = React.useState(false);
  const [deleting, setDeleting] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [formKey, setFormKey] = React.useState(0);
  const conflictConfirm = useAssigneeConflictConfirm();

  React.useEffect(() => {
    setForm(initialDraft ?? buildDefaultDraft(employees));
    setError(null);
    setFormKey((k) => k + 1);
  }, [initialDraft, employees]);

  // Klick daneben, Esc, ✕: mit geänderten Angaben erst nachfragen.
  const dirty = useDirtySnapshot(JSON.stringify(form), formKey);
  const discardGuard = useDiscardGuard({
    dirty: dirty && !form.readOnly,
    onClose,
    disabled: saving || deleting,
    description: initialDraft?.entryId
      ? 'Deine Änderungen an der Abwesenheit sind noch nicht gespeichert.'
      : 'Die Abwesenheit ist noch nicht eingetragen. Deine Eingaben gehen verloren.',
  });
  const requestClose = discardGuard.requestClose;

  React.useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Escape') return;
      // Offene Auswahllisten schließen zuerst.
      if (document.querySelector('[data-app-select-list]') || document.querySelector('[data-app-select-panel]')) return;
      requestClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [requestClose]);

  const employeeOptions = employees.map((e) => ({
    id: e.id,
    name: e.name,
    color_idx: e.color_idx,
    avatarColor: e.color,
    initials: e.initials,
    subtitle: (e.role ?? '').trim() || undefined,
  }));

  const typeLabel =
    PLANTAFEL_ABSENCE_ENTRY_TYPES.find((o) => o.entryType === form.entryType)?.label ?? form.entryType;
  const showTimes = isHourTrackingEntryType(form.entryType);
  const readOnly = Boolean(form.readOnly);
  const isRange = form.dateFrom !== form.dateTo;
  const canDelete = !readOnly && (Boolean(form.entryId) || isRange);

  const save = async (confirmConflicts = false) => {
    if (!form.employeeId || !form.dateFrom) return;
    setSaving(true);
    setError(null);
    // Editing an existing entry without moving it: the overlapping activities were already confirmed.
    const unchangedPeriod =
      Boolean(initialDraft?.entryId) &&
      initialDraft?.dateFrom === form.dateFrom &&
      initialDraft?.dateTo === form.dateTo;
    try {
      const res = await fetch('/api/plantafel/absences', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          employee_id: form.employeeId,
          date_from: form.dateFrom,
          date_to: form.dateTo || form.dateFrom,
          entry_type: form.entryType,
          start_time: showTimes ? form.startTime || null : null,
          end_time: showTimes ? form.endTime || null : null,
          break_minutes: showTimes ? normalizeBreakMinutes(Number(form.breakMinutes) || 0) : 0,
          note: form.note.trim(),
          ...(confirmConflicts || unchangedPeriod ? { confirm_conflicts: true } : {}),
        }),
      });
      if (await conflictConfirm.intercept(res, () => save(true), undefined, ABSENCE_CONFLICT_TEXTS)) return;
      const data = (await res.json()) as { error?: string };
      if (!res.ok) throw new Error(data.error || 'Speichern fehlgeschlagen.');
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Speichern fehlgeschlagen.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!form.employeeId || !form.dateFrom) return;
    setDeleting(true);
    setError(null);
    try {
      if (form.entryId && !isRange) {
        const res = await fetch(
          `/api/plantafel/absences/${encodeURIComponent(form.entryId)}?employee_id=${encodeURIComponent(form.employeeId)}`,
          { method: 'DELETE' },
        );
        const data = (await res.json()) as { error?: string };
        if (!res.ok) throw new Error(data.error || 'Löschen fehlgeschlagen.');
      } else {
        const q = new URLSearchParams({
          employee_id: form.employeeId,
          date_from: form.dateFrom,
          date_to: form.dateTo || form.dateFrom,
        });
        const res = await fetch(`/api/plantafel/absences?${q}`, { method: 'DELETE' });
        const data = (await res.json()) as { error?: string };
        if (!res.ok) throw new Error(data.error || 'Löschen fehlgeschlagen.');
      }
      onSaved();
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Löschen fehlgeschlagen.');
    } finally {
      setDeleting(false);
    }
  };

  return createPortal(
    <>
      <div
        className={`fixed inset-0 ${UI_Z_CLASS.plantafelDrawer} flex justify-end bg-black/25`}
        role="presentation"
        {...discardGuard.backdropProps}
      >
        <div
          className="flex h-full w-[min(400px,100%)] flex-col border-l border-app-border bg-app-surface shadow-xl"
          role="dialog"
          aria-modal="true"
          onClick={(e) => e.stopPropagation()}
        >
        <div className="flex items-start justify-between border-b border-app-border p-4">
          <div className="min-w-0 flex-1 pr-2">
            <p className="text-xs font-medium text-app-text-subtle">Abwesenheit</p>
            <h3 className="text-lg font-semibold text-app-text">
              {readOnly ? typeLabel : initialDraft ? 'Abwesenheit bearbeiten' : 'Abwesenheit eintragen'}
            </h3>
            {readOnly ? (
              <p className="mt-1 text-xs text-app-text-subtle">Feiertage werden über die Betriebseinstellungen gepflegt.</p>
            ) : (
              <p className="mt-1 text-xs text-app-text-subtle">
                Wird automatisch in der Zeiterfassung des Mitarbeiters gespeichert.
              </p>
            )}
          </div>
          <button type="button" className="rounded p-1 text-app-text-subtle hover:bg-app-hover" onClick={requestClose}>
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          {error ? <p className="text-sm text-red-600">{error}</p> : null}

          <div>
            <span className="mb-1 block text-sm font-medium text-app-text-muted">Mitarbeiter</span>
            <EmployeeMultiSelect
              employees={employeeOptions}
              selected={form.employeeId ? [form.employeeId] : []}
              onChange={(ids) => setForm((f) => ({ ...f, employeeId: ids.slice(-1)[0] ?? '' }))}
              selectionMode="explicit"
              emptyListLabel="Keine Mitarbeiter"
              width={320}
              disabled={readOnly || Boolean(initialDraft?.employeeId && initialDraft.entryId)}
            />
          </div>

          <div className="grid grid-cols-2 gap-2">
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-app-text-muted">Von</span>
              <DatePicker
                value={form.dateFrom}
                onChange={(v) => setForm((f) => ({ ...f, dateFrom: v, dateTo: f.dateTo < v ? v : f.dateTo }))}
                className="w-full"
                placeholder="TT.MM.JJJJ"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block font-medium text-app-text-muted">Bis</span>
              <DatePicker
                value={form.dateTo}
                onChange={(v) => setForm((f) => ({ ...f, dateTo: v }))}
                className="w-full"
                placeholder="TT.MM.JJJJ"
              />
            </label>
          </div>

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-app-text-muted">Typ</span>
            <AppSelectDropdown
              value={form.entryType}
              onChange={(v) =>
                setForm((f) => ({
                  ...f,
                  entryType: v as EmployeeTimeEntryType,
                  startTime: '',
                  endTime: '',
                }))
              }
              options={PLANTAFEL_ABSENCE_ENTRY_TYPES.map((o) => ({ value: o.entryType, label: o.label }))}
              disabled={readOnly}
            />
          </label>

          {showTimes ? (
            <div className="grid grid-cols-2 gap-2">
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-app-text-muted">Von (Uhrzeit)</span>
                <input
                  type="time"
                  className="w-full rounded-md border border-app-border px-3 py-2 text-sm disabled:bg-app-surface-muted"
                  value={form.startTime}
                  onChange={(e) => setForm((f) => ({ ...f, startTime: e.target.value }))}
                  disabled={readOnly}
                />
              </label>
              <label className="block text-sm">
                <span className="mb-1 block font-medium text-app-text-muted">Bis (Uhrzeit)</span>
                <input
                  type="time"
                  className="w-full rounded-md border border-app-border px-3 py-2 text-sm disabled:bg-app-surface-muted"
                  value={form.endTime}
                  onChange={(e) => setForm((f) => ({ ...f, endTime: e.target.value }))}
                  disabled={readOnly}
                />
              </label>
              <label className="col-span-2 block text-sm">
                <span className="mb-1 block font-medium text-app-text-muted">Pause (Minuten)</span>
                <input
                  type="number"
                  min={0}
                  className="w-full rounded-md border border-app-border px-3 py-2 text-sm disabled:bg-app-surface-muted"
                  value={form.breakMinutes}
                  onChange={(e) => setForm((f) => ({ ...f, breakMinutes: e.target.value }))}
                  disabled={readOnly}
                />
              </label>
            </div>
          ) : null}

          <label className="block text-sm">
            <span className="mb-1 block font-medium text-app-text-muted">Beschreibung</span>
            <textarea
              className="min-h-[72px] w-full rounded-md border border-app-border px-3 py-2 text-sm disabled:bg-app-surface-muted"
              value={form.note}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))}
              disabled={readOnly}
            />
          </label>
        </div>

        <div className="flex flex-wrap gap-2 border-t border-app-border p-3">
          {canDelete ? (
            <button
              type="button"
              className="app-btn-danger-outline inline-flex items-center gap-1.5 rounded-lg border border-red-200 px-3 py-2 text-sm font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
              disabled={deleting || saving}
              onClick={() => void remove()}
            >
              <Trash2 className="h-4 w-4" />
              {deleting ? 'Löscht…' : 'Löschen'}
            </button>
          ) : null}
          <div className="ml-auto flex gap-2">
            <button type="button" className="btn-pipedrive-secondary !py-2" onClick={onClose}>
              Abbrechen
            </button>
            {!readOnly ? (
              <button
                type="button"
                className="btn-pipedrive-green !py-2"
                disabled={saving || deleting || !form.employeeId || !form.dateFrom}
                onClick={() => void save()}
              >
                {saving ? 'Speichert…' : 'Speichern'}
              </button>
            ) : null}
          </div>
        </div>
        </div>
      </div>
      {discardGuard.dialog}
      {conflictConfirm.dialog}
    </>,
    document.body,
  );
}
